import { TIME_INPUT_MODES, normalizeEntityType } from "../types";
import type { CustomQuestion, Entity, EventOption, TimeInputMode, TypeSettings } from "../types";
import { COMMON_EVENT_OPTIONS, DEFAULT_EVENT_OPTIONS } from "./defaultEventOptions";
import { getCustomQuestions } from "../utils/customQuestions";
import { isFlight } from "../utils/tripDisplay";

/**
 * Defaults per activity type, in three layers: a place's own setting wins,
 * then the type's saved settings (`typeSettings/{TYPE}`, edited in the app on
 * the type's page), then what's built in here in `src/config`.
 */

export const TIME_INPUT_LABELS: Record<TimeInputMode, string> = {
  posted_wait: "Posted Wait",
  scheduled_time: "Scheduled Time",
  flight_times: "Departure & Boarding",
  none: "None",
};

export const TIME_INPUT_HINTS: Record<TimeInputMode, string> = {
  posted_wait: "Users will be prompted for the posted/displayed wait time when logging an activity.",
  scheduled_time: "Users will be prompted for a scheduled time (e.g. show time) when logging an activity.",
  flight_times: "Users will be prompted for the scheduled departure and boarding times (e.g. a flight).",
  none: "No time input will be prompted when logging an activity.",
};

/** The built-in time input for a type, before anything is saved. */
export function builtInTimeInputMode(type: string | null | undefined): TimeInputMode {
  const current = normalizeEntityType(type);
  // A show, a tasting or an excursion runs to a schedule, so what's worth
  // knowing is its start time, not a posted wait.
  if (current === "EXPERIENCE") return "scheduled_time";
  return current === "ATTRACTION" || current === "RESTAURANT" ? "posted_wait" : "none";
}

/** The built-in log steps for a type. */
export function builtInEventOptions(type: string | null | undefined): EventOption[] {
  const current = normalizeEntityType(type);
  return current ? DEFAULT_EVENT_OPTIONS[current] : COMMON_EVENT_OPTIONS;
}

function isTimeInputMode(v: unknown): v is TimeInputMode {
  return typeof v === "string" && (TIME_INPUT_MODES as readonly string[]).includes(v);
}

/** A type's log steps: its saved ones, else the built-in ones. */
export function typeEventOptions(
  type: string | null | undefined,
  settings: TypeSettings | null | undefined,
): EventOption[] {
  const saved = settings?.eventOptions;
  return Array.isArray(saved) && saved.length > 0 ? saved : builtInEventOptions(type);
}

/** A type's time input: its saved one, else the built-in one. */
export function typeTimeInputMode(
  type: string | null | undefined,
  settings: TypeSettings | null | undefined,
): TimeInputMode {
  return isTimeInputMode(settings?.timeInputMode) ? settings.timeInputMode : builtInTimeInputMode(type);
}

/** A type's questions (there are none built in). */
export function typeCustomQuestions(settings: TypeSettings | null | undefined): CustomQuestion[] {
  return getCustomQuestions({ customConfig: { custom_questions: settings?.customQuestions } });
}

/**
 * What an activity asks for its time. A place's own choice wins; then its
 * type's. An activity with no place keeps asking nothing unless its type has
 * been given a mode in the app — the built-in ones were only ever meant for
 * places with posted waits.
 */
export function resolveTimeInputMode(
  entity: (Pick<Entity, "customConfig" | "type"> & { displayName?: string | null }) | null | undefined,
  type: string | null | undefined,
  settings: TypeSettings | null | undefined,
): TimeInputMode {
  const mode = resolveSetTimeInputMode(entity, type, settings);
  // A flight asks for boarding beside departure. Flight places set up before
  // that existed were given a plain scheduled time (or nothing); both read as
  // flight times, which keep the departure in the same field.
  if (entity && isFlight(entity.type ?? type, entity.displayName) && (mode === "scheduled_time" || mode === "none")) {
    return "flight_times";
  }
  return mode;
}

function resolveSetTimeInputMode(
  entity: Pick<Entity, "customConfig" | "type"> | null | undefined,
  type: string | null | undefined,
  settings: TypeSettings | null | undefined,
): TimeInputMode {
  const own = entity?.customConfig?.time_input_mode;
  if (isTimeInputMode(own)) return own;
  if (isTimeInputMode(settings?.timeInputMode)) return settings.timeInputMode;
  return entity ? builtInTimeInputMode(entity.type ?? type) : "none";
}

/** The questions an activity asks: its place's own, else its type's. */
export function resolveCustomQuestions(
  entity: Pick<Entity, "customConfig"> | null | undefined,
  settings: TypeSettings | null | undefined,
): CustomQuestion[] {
  const own = getCustomQuestions(entity);
  return own.length > 0 ? own : typeCustomQuestions(settings);
}
